import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Send, CheckCircle, Loader2, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteForm = () => {
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    cleaning_type: '',
    surface: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const cities = [
    "Fréjus",
    "Saint-Raphaël",
    "Sainte-Maxime",
    "Agay",
    "Saint-Aygulf",
    "Les Issambres",
    "Roquebrune-sur-Argens",
    "Cannes"
  ];

  const cleaningTypes = [
    { value: "appartement", label: t("Appartement", "Apartment") },
    { value: "villa", label: "Villa" },
    { value: "airbnb", label: "Airbnb" }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/quote`, formData);
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        cleaning_type: '',
        surface: '',
        message: '',
      });
    } catch (err) {
      setError(t(
        "Une erreur s'est produite. Veuillez réessayer ou nous contacter par téléphone.",
        'An error occurred. Please try again or contact us by phone.'
      ));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="devis" className="py-20 bg-[#faf8f5]" data-testid="quote-section-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-12 text-center shadow-xl border border-stone-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('Merci pour votre demande !', 'Thank you for your request!')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t(
                'Nous vous répondrons très rapidement avec votre devis personnalisé.',
                'We will respond very quickly with your personalized quote.'
              )}
            </p>
            <button
              onClick={() => setIsSuccess(false)}
              className="bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] text-white px-6 py-3 rounded-full font-medium transition-all shadow-md"
              data-testid="quote-another-btn"
            >
              {t('Faire une autre demande', 'Make another request')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="devis" className="py-20 bg-[#faf8f5]" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
            {t('Devis Gratuit', 'Free Quote')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('Demande de devis rapide', 'Quick Quote Request')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] mx-auto mt-6 rounded-full" />
          <p className="text-slate-600 mt-6 max-w-2xl mx-auto">
            {t(
              'Remplissez le formulaire ci-dessous et recevez votre devis personnalisé sous 24h.',
              'Fill out the form below and receive your personalized quote within 24h.'
            )}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-stone-100">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            {/* Name */}
            <div>
              <label htmlFor="quote-name" className="block text-sm font-medium text-[#1a365d] mb-2">
                {t('Nom complet', 'Full name')} *
              </label>
              <input
                type="text"
                id="quote-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                placeholder={t('Votre nom', 'Your name')}
                data-testid="quote-form-name"
              />
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="quote-email" className="block text-sm font-medium text-[#1a365d] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="quote-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                  placeholder="email@exemple.com"
                  data-testid="quote-form-email"
                />
              </div>
              <div>
                <label htmlFor="quote-phone" className="block text-sm font-medium text-[#1a365d] mb-2">
                  {t('Téléphone', 'Phone')} *
                </label>
                <input
                  type="tel"
                  id="quote-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                  placeholder="+33 6 00 00 00 00"
                  data-testid="quote-form-phone"
                />
              </div>
            </div>

            {/* City & Cleaning Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="quote-city" className="block text-sm font-medium text-[#1a365d] mb-2">
                  {t('Ville', 'City')} *
                </label>
                <select
                  id="quote-city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white"
                  data-testid="quote-form-city"
                >
                  <option value="">{t('Sélectionnez une ville', 'Select a city')}</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-type" className="block text-sm font-medium text-[#1a365d] mb-2">
                  {t('Type de nettoyage', 'Cleaning type')} *
                </label>
                <select
                  id="quote-type"
                  name="cleaning_type"
                  value={formData.cleaning_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white"
                  data-testid="quote-form-type"
                >
                  <option value="">{t('Sélectionnez', 'Select')}</option>
                  {cleaningTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Surface */}
            <div>
              <label htmlFor="quote-surface" className="block text-sm font-medium text-[#1a365d] mb-2">
                {t('Surface (m²)', 'Surface (m²)')} *
              </label>
              <input
                type="text"
                id="quote-surface"
                name="surface"
                value={formData.surface}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                placeholder={t('Ex: 80', 'Ex: 80')}
                data-testid="quote-form-surface"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="quote-message" className="block text-sm font-medium text-[#1a365d] mb-2">
                {t('Message (optionnel)', 'Message (optional)')}
              </label>
              <textarea
                id="quote-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all resize-none"
                placeholder={t('Décrivez votre besoin...', 'Describe your need...')}
                data-testid="quote-form-message"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] disabled:from-[#D4AF37]/70 disabled:to-[#C9A227]/70 text-white py-4 rounded-full font-semibold text-lg transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              data-testid="quote-form-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('Envoi en cours...', 'Sending...')}
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  {t('Recevoir un devis rapide', 'Get a quick quote')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
